import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  RadioGroup,
  Radio,
  Divider,
  InputAdornment,
  Avatar,
  Badge,
  Tooltip,
  Fade,
  Collapse,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Menu,
  MenuList,
  MenuItem as MenuItemComponent,
  Snackbar,
  LinearProgress,
  Tabs,
  Tab,
  Stack,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Search,
  Visibility,
  Print,
  Receipt,
  Person,
  Edit,
  Email,
  WhatsApp,
  FilterList,
  Clear,
  MoreVert,
  Download,
  Share,
  Send,
  Delete,
  Cancel,
  Add,
  TrendingUp,
  TrendingDown,
  AttachMoney,
  CalendarToday,
  Business,
  Phone,
  LocationOn,
  CheckCircle,
  Pending,
  Refresh,
  Sort,
  ExpandMore,
  ExpandLess,
  Dashboard,
  Assessment,
  Inventory,
  Payment,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getInvoices, getInvoice, generateInvoicePDF, generateBhavaniJewelleryPDF, cancelInvoice, receivePayment } from '../redux/actions/invoiceActions';
import { sendInvoiceToCustomer } from '../redux/actions/communicationActions';
import { getActivePaymentModes } from '../redux/actions/paymentModeActions';

const InvoiceList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const { invoices, currentInvoice, pagination, loading, error } = useSelector((state) => state.invoices);
  const { activePaymentModes } = useSelector((state) => state.paymentModes);

  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [sendInvoiceDialog, setSendInvoiceDialog] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [receivePaymentDialogOpen, setReceivePaymentDialogOpen] = useState(false);
  const [paymentFormData, setPaymentFormData] = useState({
    paymentModes: []
  });
  const [cancelFormData, setCancelFormData] = useState({
    cancellationReason: 'wrong_entry', // 'wrong_entry' or 'customer_return'
    remarks: '',
    paymentModes: [], // Array of { payment_mode_id, amount, reference_number, bank_details }
  });
  const [sendMethods, setSendMethods] = useState(['email']);
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    customerId: '',
    paymentMode: '',
    minAmount: '',
    maxAmount: '',
    status: 'active', // 'active', 'cancelled', or 'all'
    paymentStatus: '' // 'pending', 'partial', 'paid', or ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [tabValue, setTabValue] = useState(0);
  const [stats, setStats] = useState({
    totalInvoices: 0,
    totalAmount: 0,
    paidInvoices: 0,
    pendingInvoices: 0
  });

  useEffect(() => {
    const params = {
      page: page + 1,
      limit: rowsPerPage,
      search: searchTerm,
      sortBy,
      sortOrder,
      ...filters
    };
    dispatch(getInvoices(params));
    dispatch(getActivePaymentModes()); // Load payment modes for cancel dialog
  }, [dispatch, page, rowsPerPage, searchTerm, filters, sortBy, sortOrder]);

  // Calculate stats from invoices
  useEffect(() => {
    if (invoices && Array.isArray(invoices)) {
      const totalAmount = invoices.reduce((sum, invoice) => sum + parseFloat(invoice.grand_total || 0), 0);
      const paidInvoices = invoices.filter(invoice => invoice.payment_status === 'paid').length;
      const pendingInvoices = invoices.filter(invoice => invoice.payment_status === 'pending').length;
      
      setStats({
        totalInvoices: pagination?.total || invoices.length || 0,
        totalAmount: totalAmount || 0,
        paidInvoices: paidInvoices || 0,
        pendingInvoices: pendingInvoices || 0
      });
    } else {
      setStats({
        totalInvoices: 0,
        totalAmount: 0,
        paidInvoices: 0,
        pendingInvoices: 0
      });
    }
  }, [invoices, pagination]);

  const handleViewInvoice = async (invoiceId) => {
    const result = await dispatch(getInvoice(invoiceId));
    if (result.success) {
      setSelectedInvoice(result.data);
      setViewDialogOpen(true);
    }
  };

  const handlePrintInvoice = async (invoiceId) => {
    try {
      await dispatch(generateBhavaniJewelleryPDF(invoiceId));
      setSnackbar({ open: true, message: 'Invoice PDF generated successfully!', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to generate PDF', severity: 'error' });
    }
  };

  const handleEditInvoice = (invoiceId) => {
    navigate(`/invoices/edit/${invoiceId}`);
  };

  const handleSendInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setSendInvoiceDialog(true);
  };

  const handleSendInvoiceConfirm = async () => {
    if (selectedInvoice && sendMethods.length > 0) {
      // Validate WhatsApp number if WhatsApp is selected and customer doesn't have phone
      if (sendMethods.includes('whatsapp') && !selectedInvoice?.customer?.phone) {
        if (!whatsappNumber || whatsappNumber.trim() === '') {
          setSnackbar({ open: true, message: 'Please enter WhatsApp number to send invoice via WhatsApp', severity: 'error' });
          return;
        }
      }

      const result = await dispatch(sendInvoiceToCustomer(
        selectedInvoice.id,
        selectedInvoice.customer_id,
        sendMethods,
        whatsappNumber || null
      ));
      
      if (result.success) {
        // Check if WhatsApp failed
        const whatsappResult = result.data?.results?.whatsapp;
        const emailResult = result.data?.results?.email;
        
        // Check if only WhatsApp was selected
        const onlyWhatsApp = sendMethods.includes('whatsapp') && !sendMethods.includes('email');
        const onlyEmail = sendMethods.includes('email') && !sendMethods.includes('whatsapp');
        
        if (whatsappResult && !whatsappResult.success && emailResult && emailResult.success) {
          // Email succeeded but WhatsApp failed
          const errorMsg = typeof whatsappResult.error === 'string' 
            ? whatsappResult.error 
            : whatsappResult.error?.message || 'WhatsApp sending failed';
          setSnackbar({ 
            open: true, 
            message: `Invoice sent via email. WhatsApp failed: ${errorMsg}`, 
            severity: 'warning' 
          });
        } else if (whatsappResult && !whatsappResult.success && (onlyWhatsApp || (!emailResult || !emailResult.success))) {
          // WhatsApp failed and it was the only method or both failed
          const errorMsg = typeof whatsappResult.error === 'string' 
            ? whatsappResult.error 
            : whatsappResult.error?.message || 'WhatsApp sending failed';
          setSnackbar({ 
            open: true, 
            message: `Failed to send invoice via WhatsApp: ${errorMsg}`, 
            severity: 'error' 
          });
        } else if (emailResult && !emailResult.success && onlyEmail) {
          // Email failed and it was the only method
          const errorMsg = typeof emailResult.error === 'string' 
            ? emailResult.error 
            : emailResult.error?.message || 'Email sending failed';
          setSnackbar({ 
            open: true, 
            message: `Failed to send invoice via email: ${errorMsg}`, 
            severity: 'error' 
          });
        } else if (result.data?.message && result.data.message.includes('failures')) {
          // Some failures
          setSnackbar({ 
            open: true, 
            message: result.data.message, 
            severity: 'warning' 
          });
        } else {
          // Success - all selected methods succeeded
          const successMethods = [];
          if (whatsappResult?.success) successMethods.push('WhatsApp');
          if (emailResult?.success) successMethods.push('Email');
          const successMsg = successMethods.length > 0 
            ? `Invoice sent successfully via ${successMethods.join(' and ')}!`
            : 'Invoice sent successfully!';
          setSnackbar({ open: true, message: successMsg, severity: 'success' });
        }
        setSendInvoiceDialog(false);
        setWhatsappNumber('');
      } else {
        setSnackbar({ open: true, message: `Failed to send invoice: ${result.message}`, severity: 'error' });
      }
    }
  };

  const handleCancelInvoice = async (invoice) => {
    // Fetch complete invoice data with payments
    try {
      const result = await dispatch(getInvoice(invoice.id));
      
      if (result.success && result.data) {
        const fullInvoice = result.data;
        setSelectedInvoice(fullInvoice);
        
        // Calculate pending amount from ACTIVE payments only
        const totalPaid = (fullInvoice.payments?.filter(p => (p.status || 'active') === 'active') || [])
          .reduce((sum, payment) => sum + (parseFloat(payment.amount || 0) || 0), 0);
        const pendingAmount = parseFloat(fullInvoice.grand_total || 0) - totalPaid;
        
        console.log('Invoice Total:', fullInvoice.grand_total);
        console.log('Total Paid:', totalPaid);
        console.log('Pending Amount:', pendingAmount);
        
        // Reset form data
        setCancelFormData({
          cancellationReason: 'wrong_entry',
          remarks: '',
          paymentModes: []
        });
        
        setCancelDialogOpen(true);
      } else {
        setSnackbar({
          open: true,
          message: result.message || 'Failed to load invoice details.',
          severity: 'error'
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to load invoice details. Please try again.',
        severity: 'error'
      });
    }
  };

  const handleCancelConfirm = async () => {
    if (selectedInvoice) {
      try {
        // Prepare cancel data based on reason
        let cancelData = {
          remarks: cancelFormData.remarks,
          cancellationReason: cancelFormData.cancellationReason
        };

        // If customer return/bargain, include payment settlement
        if (cancelFormData.cancellationReason === 'customer_return' && cancelFormData.paymentModes.length > 0) {
          const totalSettlement = cancelFormData.paymentModes.reduce((sum, pm) => sum + parseFloat(pm.amount || 0), 0);
          
          // Use first payment mode for API (or you can send all - need backend update)
          const primaryPayment = cancelFormData.paymentModes[0];
          cancelData.pendingPaymentAmount = totalSettlement;
          cancelData.pendingPaymentMode = primaryPayment.payment_mode_code || 'CASH';
          cancelData.pendingPaymentReference = primaryPayment.reference_number || '';
          cancelData.pendingPaymentBankDetails = primaryPayment.bank_details || '';
        }
        
        // Call the cancel invoice API with form data
        const result = await dispatch(cancelInvoice(selectedInvoice.id, cancelData));
        
        if (result.success) {
          setSnackbar({ 
            open: true, 
            message: `Invoice ${selectedInvoice.invoice_no} cancelled successfully!`, 
            severity: 'success' 
          });
          
          setCancelDialogOpen(false);
          setSelectedInvoice(null);
          setCancelFormData({
            cancellationReason: 'wrong_entry',
            remarks: '',
            paymentModes: []
          });
          
          // Refresh the invoice list
          dispatch(getInvoices({ page: page + 1, limit: rowsPerPage }));
        } else {
          setSnackbar({ 
            open: true, 
            message: result.message || 'Failed to cancel invoice', 
            severity: 'error' 
          });
        }
      } catch (error) {
        setSnackbar({ 
          open: true, 
          message: 'Failed to cancel invoice. Please try again.', 
          severity: 'error' 
        });
      }
    }
  };

  // Payment mode handlers for cancel dialog
  const handlePaymentModeToggle = (mode) => {
    const exists = cancelFormData.paymentModes.find(pm => pm.payment_mode_id === mode.id);
    if (exists) {
      setCancelFormData({
        ...cancelFormData,
        paymentModes: cancelFormData.paymentModes.filter(pm => pm.payment_mode_id !== mode.id)
      });
    } else {
      setCancelFormData({
        ...cancelFormData,
        paymentModes: [...cancelFormData.paymentModes, {
          payment_mode_id: mode.id,
          payment_mode_code: mode.code,
          payment_mode_name: mode.name,
          icon: mode.icon,
          amount: 0,
          reference_number: '',
          bank_details: ''
        }]
      });
    }
  };

  const handlePaymentAmountChange = (modeId, amount) => {
    setCancelFormData({
      ...cancelFormData,
      paymentModes: cancelFormData.paymentModes.map(pm =>
        pm.payment_mode_id === modeId ? { ...pm, amount: parseFloat(amount) || 0 } : pm
      )
    });
  };

  const handlePaymentReferenceChange = (modeId, reference) => {
    setCancelFormData({
      ...cancelFormData,
      paymentModes: cancelFormData.paymentModes.map(pm =>
        pm.payment_mode_id === modeId ? { ...pm, reference_number: reference } : pm
      )
    });
  };

  const handlePaymentBankDetailsChange = (modeId, bankDetails) => {
    setCancelFormData({
      ...cancelFormData,
      paymentModes: cancelFormData.paymentModes.map(pm =>
        pm.payment_mode_id === modeId ? { ...pm, bank_details: bankDetails } : pm
      )
    });
  };

  const getTotalSettlement = () => {
    return cancelFormData.paymentModes.reduce((sum, pm) => sum + parseFloat(pm.amount || 0), 0);
  };

  // Receive Payment handlers
  const handleReceivePayment = async (invoice) => {
    try {
      const result = await dispatch(getInvoice(invoice.id));
      
      if (result.success && result.data) {
        setSelectedInvoice(result.data);
        setPaymentFormData({ paymentModes: [] });
        setReceivePaymentDialogOpen(true);
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to load invoice details.',
        severity: 'error'
      });
    }
  };

  const handlePaymentModeToggleForReceive = (mode) => {
    const exists = paymentFormData.paymentModes.find(pm => pm.payment_mode_id === mode.id);
    if (exists) {
      setPaymentFormData({
        ...paymentFormData,
        paymentModes: paymentFormData.paymentModes.filter(pm => pm.payment_mode_id !== mode.id)
      });
    } else {
      setPaymentFormData({
        ...paymentFormData,
        paymentModes: [...paymentFormData.paymentModes, {
          payment_mode_id: mode.id,
          payment_mode_code: mode.code,
          payment_mode_name: mode.name,
          icon: mode.icon,
          amount: 0,
          reference_number: '',
          bank_details: ''
        }]
      });
    }
  };

  const handlePaymentAmountChangeForReceive = (modeId, amount) => {
    setPaymentFormData({
      ...paymentFormData,
      paymentModes: paymentFormData.paymentModes.map(pm =>
        pm.payment_mode_id === modeId ? { ...pm, amount: parseFloat(amount) || 0 } : pm
      )
    });
  };

  const handlePaymentReferenceChangeForReceive = (modeId, reference) => {
    setPaymentFormData({
      ...paymentFormData,
      paymentModes: paymentFormData.paymentModes.map(pm =>
        pm.payment_mode_id === modeId ? { ...pm, reference_number: reference } : pm
      )
    });
  };

  const handlePaymentBankDetailsChangeForReceive = (modeId, bankDetails) => {
    setPaymentFormData({
      ...paymentFormData,
      paymentModes: paymentFormData.paymentModes.map(pm =>
        pm.payment_mode_id === modeId ? { ...pm, bank_details: bankDetails } : pm
      )
    });
  };

  const getTotalPaymentForReceive = () => {
    return paymentFormData.paymentModes.reduce((sum, pm) => sum + parseFloat(pm.amount || 0), 0);
  };

  const handleReceivePaymentConfirm = async () => {
    if (selectedInvoice && paymentFormData.paymentModes.length > 0) {
      try {
        // Process each payment mode
        for (const pm of paymentFormData.paymentModes) {
          if (pm.amount > 0) {
            const result = await dispatch(receivePayment(selectedInvoice.id, {
              payment_mode_code: pm.payment_mode_code,
              amount: pm.amount,
              reference_number: pm.reference_number,
              bank_details: pm.bank_details
            }));
            
            if (!result.success) {
              setSnackbar({
                open: true,
                message: result.message || 'Failed to receive payment',
                severity: 'error'
              });
              return;
            }
          }
        }
        
        setSnackbar({
          open: true,
          message: 'Payment received successfully!',
          severity: 'success'
        });
        
        setReceivePaymentDialogOpen(false);
        setSelectedInvoice(null);
        setPaymentFormData({ paymentModes: [] });
        
        // Refresh invoice list
        dispatch(getInvoices({ page: page + 1, limit: rowsPerPage }));
      } catch (error) {
        setSnackbar({
          open: true,
          message: 'Failed to receive payment. Please try again.',
          severity: 'error'
        });
      }
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    setPage(0);
  };

  const handleClearFilters = () => {
    setFilters({
      startDate: '',
      endDate: '',
      customerId: '',
      paymentMode: '',
      minAmount: '',
      maxAmount: '',
      status: 'active',
      paymentStatus: ''
    });
    setPage(0);
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const handleSelectInvoice = (invoiceId) => {
    setSelectedInvoices(prev => 
      prev.includes(invoiceId) 
        ? prev.filter(id => id !== invoiceId)
        : [...prev, invoiceId]
    );
  };

  const handleSelectAll = () => {
    if (selectedInvoices.length === invoices.length) {
      setSelectedInvoices([]);
    } else {
      setSelectedInvoices(invoices.map(invoice => invoice.id));
    }
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'success';
      case 'pending': return 'warning';
      case 'overdue': return 'error';
      default: return 'default';
    }
  };

  const getPaymentStatusIcon = (status) => {
    switch (status) {
      case 'paid': return <CheckCircle />;
      case 'pending': return <Pending />;
      case 'overdue': return <Cancel />;
      default: return <Pending />;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const StatCard = ({ title, value, icon, color, trend }) => (
    <Card 
      sx={{ 
        background: `linear-gradient(135deg, ${color} 0%, ${alpha(color, 0.8)} 100%)`,
        color: 'white',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          right: 0,
          width: '100px',
          height: '100px',
          background: 'rgba(255,255,255,0.1)',
          borderRadius: '50%',
          transform: 'translate(30px, -30px)'
        }
      }}
    >
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h4" fontWeight="bold">
              {value}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              {title}
            </Typography>
          </Box>
          <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
            {icon}
          </Avatar>
        </Box>
        {trend && (
          <Box display="flex" alignItems="center" mt={1}>
            {trend > 0 ? <TrendingUp /> : <TrendingDown />}
            <Typography variant="caption" sx={{ ml: 0.5 }}>
              {Math.abs(trend)}% from last month
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  if (loading && (!invoices || invoices.length === 0)) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header Section */}
      <Box mb={4}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h4" fontWeight="bold" color="primary">
            Invoice Management
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate('/billing')}
            sx={{ 
              borderRadius: 2,
              px: 3,
              py: 1.5,
              background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
              boxShadow: '0 3px 5px 2px rgba(33, 203, 243, .3)',
            }}
          >
            Create Invoice
          </Button>
        </Box>

        {/* Stats Cards */}
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Total Invoices"
              value={stats.totalInvoices}
              icon={<Receipt />}
              color="#2196F3"
              trend={12}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Total Amount"
              value={formatCurrency(stats.totalAmount)}
              icon={<AttachMoney />}
              color="#4CAF50"
              trend={8}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Paid Invoices"
              value={stats.paidInvoices}
              icon={<CheckCircle />}
              color="#8BC34A"
              trend={15}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Pending Invoices"
              value={stats.pendingInvoices}
              icon={<Pending />}
              color="#FF9800"
              trend={-5}
            />
          </Grid>
        </Grid>

        {/* Search and Filters */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  placeholder="Search invoices, customers, or invoice numbers..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<FilterList />}
                  onClick={() => setShowFilters(!showFilters)}
                  sx={{ borderRadius: 2, py: 1.5 }}
                >
                  Filters
                </Button>
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth>
                  <InputLabel>Sort By</InputLabel>
                  <Select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    sx={{ borderRadius: 2 }}
                  >
                    <MenuItem value="created_at">Date</MenuItem>
                    <MenuItem value="grand_total">Amount</MenuItem>
                    <MenuItem value="customer_name">Customer</MenuItem>
                    <MenuItem value="invoice_number">Invoice No</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<Sort />}
                  onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                  sx={{ borderRadius: 2, py: 1.5 }}
                >
                  {sortOrder === 'asc' ? 'Ascending' : 'Descending'}
                </Button>
              </Grid>
              <Grid item xs={12} md={2}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={() => window.location.reload()}
                  sx={{ borderRadius: 2, py: 1.5 }}
                >
                  Refresh
                </Button>
              </Grid>
            </Grid>

            {/* Advanced Filters */}
            <Collapse in={showFilters}>
              <Divider sx={{ my: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    fullWidth
                    label="Start Date"
                    type="date"
                    value={filters.startDate}
                    onChange={(e) => handleFilterChange('startDate', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    fullWidth
                    label="End Date"
                    type="date"
                    value={filters.endDate}
                    onChange={(e) => handleFilterChange('endDate', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    fullWidth
                    label="Min Amount"
                    type="number"
                    value={filters.minAmount}
                    onChange={(e) => handleFilterChange('minAmount', e.target.value)}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    fullWidth
                    label="Max Amount"
                    type="number"
                    value={filters.maxAmount}
                    onChange={(e) => handleFilterChange('maxAmount', e.target.value)}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl fullWidth>
                    <InputLabel>Payment Mode</InputLabel>
                    <Select
                      value={filters.paymentMode}
                      onChange={(e) => handleFilterChange('paymentMode', e.target.value)}
                      sx={{ borderRadius: 2 }}
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="CASH">Cash</MenuItem>
                      <MenuItem value="CARD">Card</MenuItem>
                      <MenuItem value="UPI">UPI</MenuItem>
                      <MenuItem value="NET_BANKING">Net Banking</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl fullWidth>
                    <InputLabel>Invoice Status</InputLabel>
                    <Select
                      value={filters.status}
                      onChange={(e) => handleFilterChange('status', e.target.value)}
                      sx={{ borderRadius: 2 }}
                      label="Invoice Status"
                    >
                      <MenuItem value="all">All</MenuItem>
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="cancelled">Cancelled</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl fullWidth>
                    <InputLabel>Payment Status</InputLabel>
                    <Select
                      value={filters.paymentStatus}
                      onChange={(e) => handleFilterChange('paymentStatus', e.target.value)}
                      sx={{ borderRadius: 2 }}
                      label="Payment Status"
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="partial">Partial</MenuItem>
                      <MenuItem value="paid">Paid</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<Clear />}
                    onClick={handleClearFilters}
                    sx={{ borderRadius: 2, py: 1.5 }}
                  >
                    Clear
                  </Button>
                </Grid>
              </Grid>
            </Collapse>
          </CardContent>
        </Card>
      </Box>

      {/* Invoices Table */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          {loading && <LinearProgress />}
          
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedInvoices.length === invoices.length && invoices.length > 0}
                      indeterminate={selectedInvoices.length > 0 && selectedInvoices.length < invoices.length}
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                  <TableCell>
                    <Button
                      onClick={() => handleSort('invoice_number')}
                      sx={{ fontWeight: 'bold', textTransform: 'none' }}
                      endIcon={sortBy === 'invoice_number' ? (sortOrder === 'asc' ? <ExpandLess /> : <ExpandMore />) : null}
                    >
                      Invoice #
                    </Button>
                  </TableCell>
                  <TableCell>
                    <Button
                      onClick={() => handleSort('customer_name')}
                      sx={{ fontWeight: 'bold', textTransform: 'none' }}
                      endIcon={sortBy === 'customer_name' ? (sortOrder === 'asc' ? <ExpandLess /> : <ExpandMore />) : null}
                    >
                      Customer
                    </Button>
                  </TableCell>
                  <TableCell>
                    <Button
                      onClick={() => handleSort('created_at')}
                      sx={{ fontWeight: 'bold', textTransform: 'none' }}
                      endIcon={sortBy === 'created_at' ? (sortOrder === 'asc' ? <ExpandLess /> : <ExpandMore />) : null}
                    >
                      Date
                    </Button>
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      onClick={() => handleSort('grand_total')}
                      sx={{ fontWeight: 'bold', textTransform: 'none' }}
                      endIcon={sortBy === 'grand_total' ? (sortOrder === 'asc' ? <ExpandLess /> : <ExpandMore />) : null}
                    >
                      Amount
                    </Button>
                  </TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Items</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invoices && invoices.length > 0 ? invoices.map((invoice) => (
                  <TableRow 
                    key={invoice.id} 
                    hover
                    sx={{ 
                      '&:nth-of-type(odd)': { backgroundColor: alpha(theme.palette.action.hover, 0.02) },
                      '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.04) }
                    }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedInvoices.includes(invoice.id)}
                        onChange={() => handleSelectInvoice(invoice.id)}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {invoice.invoice_no}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <Avatar sx={{ width: 32, height: 32, mr: 2, bgcolor: theme.palette.primary.main }}>
                          <Person />
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {invoice.customer?.name || 'N/A'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {invoice.customer?.phone || 'No phone'}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(invoice.invoice_date)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="bold" color="primary">
                        {formatCurrency(invoice.grand_total)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        icon={getPaymentStatusIcon(invoice.payment_status)}
                        label={invoice.payment_status || 'pending'}
                        color={getPaymentStatusColor(invoice.payment_status)}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">
                        {invoice.items?.length || 0} items
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Box display="flex" gap={0.5}>
                        <Tooltip title="View">
                          <IconButton
                            size="small"
                            onClick={() => handleViewInvoice(invoice.id)}
                            sx={{ color: theme.palette.info.main }}
                          >
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Print">
                          <IconButton
                            size="small"
                            onClick={() => handlePrintInvoice(invoice.id)}
                            sx={{ color: theme.palette.warning.main }}
                          >
                            <Print />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => handleEditInvoice(invoice.id)}
                            sx={{ color: theme.palette.primary.main }}
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Send">
                          <IconButton
                            size="small"
                            onClick={() => handleSendInvoice(invoice)}
                            sx={{ color: theme.palette.success.main }}
                          >
                            <Email />
                          </IconButton>
                        </Tooltip>
                        {invoice.payment_status !== 'paid' && invoice.status !== 'cancelled' && (
                          <Tooltip title="Receive Payment">
                            <IconButton
                              size="small"
                              onClick={() => handleReceivePayment(invoice)}
                              sx={{ color: theme.palette.warning.main }}
                            >
                              <Payment />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Cancel Invoice">
                          <IconButton
                            size="small"
                            onClick={() => handleCancelInvoice(invoice)}
                            sx={{ color: theme.palette.error.main }}
                          >
                            <Cancel />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                )) : null}
                {(!invoices || invoices.length === 0) && (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <Box textAlign="center">
                        <Receipt sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                          No invoices found
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Create your first invoice to get started
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={pagination?.total || invoices?.length || 0}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Rows per page:"
            labelDisplayedRows={({ from, to, count }) => {
              if (count === 0) return '0-0 of 0';
              const actualTo = Math.min(to, count);
              return `${from}-${actualTo} of ${count}`;
            }}
          />
        </CardContent>
      </Card>

      {/* View Invoice Dialog - Recreated with Modern UI */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}
      >
        {selectedInvoice && (
          <>
            {/* Header with Gradient Background */}
            <DialogTitle sx={{
              bgcolor: 'primary.main',
              color: 'white',
              pb: 2.5,
              pt: 3,
              px: 3
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 48, height: 48 }}>
                    <Receipt sx={{ fontSize: 28 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                      Invoice Details
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Invoice #{selectedInvoice.invoice_no || selectedInvoice.invoice_number}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Tooltip title="Print Invoice">
                    <IconButton
                      onClick={() => handlePrintInvoice(selectedInvoice.id)}
                      sx={{
                        color: 'white',
                        bgcolor: 'rgba(255,255,255,0.2)',
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }
                      }}
                    >
                      <Print />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Edit Invoice">
                    <IconButton
                      onClick={() => {
                        handleEditInvoice(selectedInvoice.id);
                        setViewDialogOpen(false);
                      }}
                      sx={{
                        color: 'white',
                        bgcolor: 'rgba(255,255,255,0.2)',
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }
                      }}
                    >
                      <Edit />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Send Invoice">
                    <IconButton
                      onClick={() => {
                        handleSendInvoice(selectedInvoice);
                        setViewDialogOpen(false);
                      }}
                      sx={{
                        color: 'white',
                        bgcolor: 'rgba(255,255,255,0.2)',
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }
                      }}
                    >
                      <Email />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </DialogTitle>

            <DialogContent sx={{ p: 3 }}>
              {/* Invoice Summary Cards */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{
                    bgcolor: 'primary.50',
                    border: '1px solid',
                    borderColor: 'primary.200',
                    borderRadius: 2
                  }}>
                    <CardContent sx={{ p: 2 }}>
                      <Typography variant="caption" color="text.secondary" gutterBottom>
                        Invoice Number
                      </Typography>
                      <Typography variant="h6" fontWeight="bold" color="primary">
                        {selectedInvoice.invoice_no || selectedInvoice.invoice_number}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{
                    bgcolor: 'info.50',
                    border: '1px solid',
                    borderColor: 'info.200',
                    borderRadius: 2
                  }}>
                    <CardContent sx={{ p: 2 }}>
                      <Typography variant="caption" color="text.secondary" gutterBottom>
                        Invoice Date
                      </Typography>
                      <Typography variant="h6" fontWeight="bold" color="info.main">
                        {formatDate(selectedInvoice.invoice_date)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{
                    bgcolor: 'success.50',
                    border: '1px solid',
                    borderColor: 'success.200',
                    borderRadius: 2
                  }}>
                    <CardContent sx={{ p: 2 }}>
                      <Typography variant="caption" color="text.secondary" gutterBottom>
                        Grand Total
                      </Typography>
                      <Typography variant="h6" fontWeight="bold" color="success.main">
                        {formatCurrency(selectedInvoice.grand_total)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{
                    bgcolor: selectedInvoice.payment_status === 'paid' ? 'success.50' : selectedInvoice.payment_status === 'partial' ? 'warning.50' : 'error.50',
                    border: '1px solid',
                    borderColor: selectedInvoice.payment_status === 'paid' ? 'success.200' : selectedInvoice.payment_status === 'partial' ? 'warning.200' : 'error.200',
                    borderRadius: 2
                  }}>
                    <CardContent sx={{ p: 2 }}>
                      <Typography variant="caption" color="text.secondary" gutterBottom>
                        Payment Status
                      </Typography>
                      <Chip
                        icon={getPaymentStatusIcon(selectedInvoice.payment_status)}
                        label={selectedInvoice.payment_status || 'pending'}
                        color={getPaymentStatusColor(selectedInvoice.payment_status)}
                        size="small"
                        sx={{ mt: 0.5 }}
                      />
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Customer Information Card */}
              {selectedInvoice.customer && (
                <Card sx={{ mb: 3, borderRadius: 2, boxShadow: 2 }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}>
                        <Person />
                      </Avatar>
                      <Box>
                        <Typography variant="h6" fontWeight="bold">
                          Customer Information
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Customer details for this invoice
                        </Typography>
                      </Box>
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="caption" color="text.secondary" gutterBottom>
                          Customer Name
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {selectedInvoice.customer.name || 'N/A'}
                        </Typography>
                      </Grid>
                      {selectedInvoice.customer.phone && (
                        <Grid item xs={12} md={6}>
                          <Typography variant="caption" color="text.secondary" gutterBottom>
                            Phone Number
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Phone sx={{ fontSize: 18, color: 'text.secondary' }} />
                            <Typography variant="body1">
                              {selectedInvoice.customer.phone}
                            </Typography>
                          </Box>
                        </Grid>
                      )}
                      {selectedInvoice.customer.email && (
                        <Grid item xs={12} md={6}>
                          <Typography variant="caption" color="text.secondary" gutterBottom>
                            Email Address
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Email sx={{ fontSize: 18, color: 'text.secondary' }} />
                            <Typography variant="body1">
                              {selectedInvoice.customer.email}
                            </Typography>
                          </Box>
                        </Grid>
                      )}
                      {selectedInvoice.customer.address && (
                        <Grid item xs={12} md={6}>
                          <Typography variant="caption" color="text.secondary" gutterBottom>
                            Address
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                            <LocationOn sx={{ fontSize: 18, color: 'text.secondary', mt: 0.5 }} />
                            <Typography variant="body1">
                              {selectedInvoice.customer.address}
                            </Typography>
                          </Box>
                        </Grid>
                      )}
                      {selectedInvoice.customer.gstin && (
                        <Grid item xs={12} md={6}>
                          <Typography variant="caption" color="text.secondary" gutterBottom>
                            GSTIN
                          </Typography>
                          <Typography variant="body1" fontWeight="medium">
                            {selectedInvoice.customer.gstin}
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </CardContent>
                </Card>
              )}

              {/* Invoice Items Card */}
              {selectedInvoice.items && selectedInvoice.items.length > 0 && (
                <Card sx={{ mb: 3, borderRadius: 2, boxShadow: 2 }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Avatar sx={{ bgcolor: 'secondary.main', width: 48, height: 48 }}>
                        <Inventory />
                      </Avatar>
                      <Box>
                        <Typography variant="h6" fontWeight="bold">
                          Invoice Items ({selectedInvoice.items.length})
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Products and services in this invoice
                        </Typography>
                      </Box>
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    <TableContainer>
                      <Table size="medium">
                        <TableHead>
                          <TableRow sx={{ bgcolor: 'grey.100' }}>
                            <TableCell><strong>S.No</strong></TableCell>
                            <TableCell><strong>Item Description</strong></TableCell>
                            <TableCell align="center"><strong>HUID</strong></TableCell>
                            <TableCell align="center"><strong>HSN Code</strong></TableCell>
                            <TableCell align="center"><strong>Quantity</strong></TableCell>
                            <TableCell align="right"><strong>Weight (g)</strong></TableCell>
                            <TableCell align="right"><strong>Rate (₹/g)</strong></TableCell>
                            <TableCell align="right"><strong>GST %</strong></TableCell>
                            <TableCell align="right"><strong>Amount (₹)</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {selectedInvoice.items.map((item, index) => (
                            <TableRow key={index} hover>
                              <TableCell>{index + 1}</TableCell>
                              <TableCell>
                                <Typography variant="body2" fontWeight="medium">
                                  {item.description || item.product?.name || 'N/A'}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                {item.huid || item.product?.huid || '—'}
                              </TableCell>
                              <TableCell align="center">
                                {item.product?.hsn_code || selectedInvoice.customer?.hsn_code || '—'}
                              </TableCell>
                              <TableCell align="center">
                                <Chip label={item.qty || 1} size="small" color="primary" variant="outlined" />
                              </TableCell>
                              <TableCell align="right">
                                {parseFloat(item.weight || 0).toFixed(3)}
                              </TableCell>
                              <TableCell align="right">
                                {formatCurrency(parseFloat(item.rate || 0))}
                              </TableCell>
                              <TableCell align="right">
                                {item.gst_percentage ? `${item.gst_percentage}%` : '—'}
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" fontWeight="bold" color="primary">
                                  {formatCurrency(item.amount || item.total_amount || item.final_amount || 0)}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              )}

              {/* Payment Information Card */}
              {selectedInvoice.payments && selectedInvoice.payments.length > 0 && (
                <Card sx={{ mb: 3, borderRadius: 2, boxShadow: 2 }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Avatar sx={{ bgcolor: 'success.main', width: 48, height: 48 }}>
                        <Payment />
                      </Avatar>
                      <Box>
                        <Typography variant="h6" fontWeight="bold">
                          Payment Information
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Payment transactions for this invoice
                        </Typography>
                      </Box>
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: 'grey.100' }}>
                            <TableCell><strong>Payment Mode</strong></TableCell>
                            <TableCell align="right"><strong>Amount (₹)</strong></TableCell>
                            <TableCell align="center"><strong>Reference</strong></TableCell>
                            <TableCell align="center"><strong>Date</strong></TableCell>
                            <TableCell align="center"><strong>Status</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {selectedInvoice.payments
                            .filter(p => (p.status || 'active') === 'active')
                            .map((payment, index) => (
                            <TableRow key={index} hover>
                              <TableCell>
                                <Chip 
                                  label={payment.payment_mode?.name || payment.payment_mode_code || 'N/A'} 
                                  size="small" 
                                  color="primary" 
                                  variant="outlined"
                                />
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" fontWeight="bold">
                                  {formatCurrency(payment.amount)}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                {payment.reference_number || payment.reference || '—'}
                              </TableCell>
                              <TableCell align="center">
                                {formatDate(payment.created_at || payment.payment_date)}
                              </TableCell>
                              <TableCell align="center">
                                <Chip
                                  label={payment.status || 'active'}
                                  size="small"
                                  color={payment.status === 'active' ? 'success' : 'default'}
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    
                    {/* Payment Summary */}
                    <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                          <Typography variant="caption" color="text.secondary">Total Amount</Typography>
                          <Typography variant="h6" fontWeight="bold">
                            {formatCurrency(selectedInvoice.grand_total)}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Typography variant="caption" color="text.secondary">Paid Amount</Typography>
                          <Typography variant="h6" fontWeight="bold" color="success.main">
                            {formatCurrency(
                              (selectedInvoice.payments?.filter(p => (p.status || 'active') === 'active') || [])
                                .reduce((sum, p) => sum + (parseFloat(p.amount || 0) || 0), 0)
                            )}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Typography variant="caption" color="text.secondary">Balance Amount</Typography>
                          <Typography variant="h6" fontWeight="bold" color={
                            (parseFloat(selectedInvoice.grand_total || 0) - 
                            ((selectedInvoice.payments?.filter(p => (p.status || 'active') === 'active') || [])
                              .reduce((sum, p) => sum + (parseFloat(p.amount || 0) || 0), 0))) > 0 
                            ? 'error.main' : 'success.main'
                          }>
                            {formatCurrency(
                              parseFloat(selectedInvoice.grand_total || 0) - 
                              ((selectedInvoice.payments?.filter(p => (p.status || 'active') === 'active') || [])
                                .reduce((sum, p) => sum + (parseFloat(p.amount || 0) || 0), 0))
                            )}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>
                  </CardContent>
                </Card>
              )}

              {/* Invoice Summary Card */}
              <Card sx={{ borderRadius: 2, boxShadow: 2, bgcolor: 'primary.50' }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Invoice Summary
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">Subtotal:</Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {formatCurrency(selectedInvoice.subtotal || selectedInvoice.total_amount || 0)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">Tax Amount:</Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {formatCurrency(selectedInvoice.tax_amount || selectedInvoice.gst_amount || 0)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">Discount:</Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {formatCurrency(selectedInvoice.discount || 0)}
                        </Typography>
                      </Box>
                      <Divider sx={{ my: 1 }} />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="h6" fontWeight="bold" color="primary">Grand Total:</Typography>
                        <Typography variant="h6" fontWeight="bold" color="primary">
                          {formatCurrency(selectedInvoice.grand_total)}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">Invoice Status:</Typography>
                        <Chip
                          label={selectedInvoice.status === 'cancelled' ? 'Cancelled' : 'Active'}
                          color={selectedInvoice.status === 'cancelled' ? 'error' : 'success'}
                          size="small"
                        />
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">Payment Status:</Typography>
                        <Chip
                          icon={getPaymentStatusIcon(selectedInvoice.payment_status)}
                          label={selectedInvoice.payment_status || 'pending'}
                          color={getPaymentStatusColor(selectedInvoice.payment_status)}
                          size="small"
                        />
                      </Box>
                      {selectedInvoice.order_reference && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2" color="text.secondary">Order Reference:</Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {selectedInvoice.order_reference}
                          </Typography>
                        </Box>
                      )}
                      {selectedInvoice.created_at && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Created At:</Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {formatDate(selectedInvoice.created_at)}
                          </Typography>
                        </Box>
                      )}
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 3, pt: 2, gap: 1.5 }}>
              <Button
                onClick={() => setViewDialogOpen(false)}
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  px: 3,
                  minWidth: 100
                }}
              >
                Close
              </Button>
              {selectedInvoice.payment_status !== 'paid' && selectedInvoice.status !== 'cancelled' && (
                <Button
                  onClick={() => {
                    handleReceivePayment(selectedInvoice);
                    setViewDialogOpen(false);
                  }}
                  variant="contained"
                  color="success"
                  startIcon={<Payment />}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    px: 3
                  }}
                >
                  Receive Payment
                </Button>
              )}
              <Button
                onClick={() => handlePrintInvoice(selectedInvoice.id)}
                variant="contained"
                color="primary"
                startIcon={<Print />}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  px: 3
                }}
              >
                Print Invoice
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Send Invoice Dialog */}
      <Dialog
        open={sendInvoiceDialog}
        onClose={() => {
          setSendInvoiceDialog(false);
          setSendMethods(['email']);
          setWhatsappNumber('');
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: 'primary.main',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <Share />
          <Typography variant="h6" fontWeight="bold">
            Send Invoice to Customer
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {selectedInvoice?.customer && (
            <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Customer:
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {selectedInvoice.customer.name}
              </Typography>
              {selectedInvoice.customer.email && (
                <Typography variant="body2" color="text.secondary">
                  📧 {selectedInvoice.customer.email}
                </Typography>
              )}
              {selectedInvoice.customer.phone && (
                <Typography variant="body2" color="text.secondary">
                  📱 {selectedInvoice.customer.phone}
                </Typography>
              )}
            </Box>
          )}
          
          <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
            Select communication methods to send invoice:
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Card 
              sx={{ 
                border: sendMethods.includes('email') ? '2px solid' : '1px solid',
                borderColor: sendMethods.includes('email') ? 'primary.main' : 'divider',
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' }
              }}
              onClick={() => {
                if (sendMethods.includes('email')) {
                  setSendMethods(sendMethods.filter(m => m !== 'email'));
                } else {
                  setSendMethods([...sendMethods, 'email']);
                }
              }}
            >
              <CardContent>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={sendMethods.includes('email')}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSendMethods([...sendMethods, 'email']);
                        } else {
                          setSendMethods(sendMethods.filter(m => m !== 'email'));
                        }
                      }}
                      color="primary"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Email color="primary" />
                      <Typography variant="body1" fontWeight="medium">
                        Email
                      </Typography>
                    </Box>
                  }
                />
                {selectedInvoice?.customer?.email ? (
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 4 }}>
                    Will be sent to: {selectedInvoice.customer.email}
                  </Typography>
                ) : (
                  <Typography variant="caption" color="error" sx={{ ml: 4 }}>
                    Customer email not available
                  </Typography>
                )}
              </CardContent>
            </Card>

            <Card 
              sx={{ 
                border: sendMethods.includes('whatsapp') ? '2px solid' : '1px solid',
                borderColor: sendMethods.includes('whatsapp') ? 'success.main' : 'divider',
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': { borderColor: 'success.main', bgcolor: 'action.hover' }
              }}
              onClick={() => {
                if (sendMethods.includes('whatsapp')) {
                  setSendMethods(sendMethods.filter(m => m !== 'whatsapp'));
                } else {
                  setSendMethods([...sendMethods, 'whatsapp']);
                }
              }}
            >
              <CardContent>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={sendMethods.includes('whatsapp')}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSendMethods([...sendMethods, 'whatsapp']);
                        } else {
                          setSendMethods(sendMethods.filter(m => m !== 'whatsapp'));
                        }
                      }}
                      color="success"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <WhatsApp sx={{ color: 'success.main' }} />
                      <Typography variant="body1" fontWeight="medium">
                        WhatsApp
                      </Typography>
                    </Box>
                  }
                />
                {selectedInvoice?.customer?.phone ? (
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 4 }}>
                    Will be sent to: {selectedInvoice.customer.phone}
                  </Typography>
                ) : (
                  <Box sx={{ ml: 4, mt: 1 }}>
                    <Typography variant="caption" color="error" sx={{ display: 'block', mb: 1 }}>
                      Customer phone number not available
                    </Typography>
                    <TextField
                      size="small"
                      fullWidth
                      label="Enter WhatsApp Number"
                      value={whatsappNumber}
                      onChange={(e) => setWhatsappNumber(e.target.value)}
                      placeholder="+919876543210"
                      helperText="Include country code (e.g., +91 for India)"
                      required={sendMethods.includes('whatsapp')}
                      error={sendMethods.includes('whatsapp') && !whatsappNumber}
                      sx={{ mt: 1 }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <WhatsApp sx={{ color: 'success.main' }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>
                )}
              </CardContent>
            </Card>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button 
            onClick={() => {
              setSendInvoiceDialog(false);
              setSendMethods(['email']);
              setWhatsappNumber('');
            }}
            variant="outlined"
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSendInvoiceConfirm} 
            variant="contained"
            disabled={sendMethods.length === 0}
            startIcon={<Send />}
            sx={{ 
              borderRadius: 2, 
              textTransform: 'none',
              px: 3
            }}
          >
            Send Invoice
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Invoice Dialog */}
      <Dialog
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #f44336 0%, #e91e63 100%)',
          color: 'white',
          pb: 3
        }}>
          <Box display="flex" alignItems="center" gap={2}>
            <Cancel sx={{ fontSize: 32 }} />
            <Box>
              <Typography variant="h5" fontWeight="bold">Cancel Invoice</Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                Invoice #{selectedInvoice?.invoice_no}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          {/* Invoice Summary Card */}
          {selectedInvoice?.customer && (
            <Card sx={{ mb: 3, background: 'linear-gradient(135deg, #f5f5f5 0%, #eeeeee 100%)' }}>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      <Person sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                      Customer
                    </Typography>
                    <Typography variant="h6">{selectedInvoice.customer.name}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Grid container spacing={1}>
                      <Grid item xs={4}>
                        <Typography variant="caption" color="text.secondary">Total</Typography>
                        <Typography variant="body1" fontWeight="bold">
                          ₹{selectedInvoice.grand_total ? Number(selectedInvoice.grand_total).toFixed(2) : '0.00'}
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="caption" color="text.secondary">Paid</Typography>
                        <Typography variant="body1" fontWeight="bold" color="success.main">
                          ₹{(((selectedInvoice.payments?.filter(p => (p.status || 'active') === 'active') || [])
                            .reduce((sum, p) => sum + (parseFloat(p.amount || 0) || 0), 0)) || 0).toFixed(2)}
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="caption" color="text.secondary">Pending</Typography>
                        <Typography variant="body1" fontWeight="bold" color="error">
                          ₹{(parseFloat(selectedInvoice.grand_total || 0) - (((selectedInvoice.payments?.filter(p => (p.status || 'active') === 'active') || [])
                            .reduce((sum, p) => sum + (parseFloat(p.amount || 0) || 0), 0)) || 0)).toFixed(2)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          )}

          {/* Cancellation Reason */}
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
            1. Cancellation Reason *
          </Typography>
          <RadioGroup
            value={cancelFormData.cancellationReason}
            onChange={(e) => setCancelFormData({ ...cancelFormData, cancellationReason: e.target.value, paymentModes: [] })}
            sx={{ mb: 2 }}
          >
            <FormControlLabel 
              value="wrong_entry" 
              control={<Radio />} 
              label={
                <Box>
                  <Typography variant="body1">Wrong Entry / Data Entry Error</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Invoice was created with incorrect information
                  </Typography>
                </Box>
              } 
            />
            <FormControlLabel 
              value="customer_return" 
              control={<Radio />} 
              label={
                <Box>
                  <Typography variant="body1">Customer Return / Bargain</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Customer returned products or negotiated different price
                  </Typography>
                </Box>
              } 
            />
          </RadioGroup>

          {/* Remarks */}
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mt: 3 }}>
            2. Cancellation Remarks *
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            value={cancelFormData.remarks}
            onChange={(e) => setCancelFormData({ ...cancelFormData, remarks: e.target.value })}
            placeholder="Enter detailed reason for cancellation..."
            sx={{ mb: 3 }}
            required
          />

          {/* Payment Settlement - Only for Customer Return */}
          {cancelFormData.cancellationReason === 'customer_return' && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                3. Payment Settlement (Optional)
              </Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                Select payment modes to settle pending amount or receive refund
              </Alert>
              
              {/* Payment Mode Selection */}
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Select Payment Modes:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                {activePaymentModes?.map((mode) => (
                  <Chip
                    key={mode.id}
                    label={mode.name}
                    onClick={() => handlePaymentModeToggle(mode)}
                    color={cancelFormData.paymentModes.find(pm => pm.payment_mode_id === mode.id) ? 'primary' : 'default'}
                    variant={cancelFormData.paymentModes.find(pm => pm.payment_mode_id === mode.id) ? 'filled' : 'outlined'}
                    sx={{ cursor: 'pointer' }}
                  />
                ))}
              </Box>

              {/* Payment Mode Details */}
              {cancelFormData.paymentModes.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  {cancelFormData.paymentModes.map((pm) => (
                    <Card key={pm.payment_mode_id} sx={{ mb: 2, border: '1px solid', borderColor: 'divider' }}>
                      <CardContent>
                        <Typography variant="subtitle2" gutterBottom>
                          {pm.payment_mode_name}
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={4}>
                            <TextField
                              fullWidth
                              label="Amount"
                              type="number"
                              value={pm.amount || ''}
                              onChange={(e) => handlePaymentAmountChange(pm.payment_mode_id, e.target.value)}
                              InputProps={{
                                startAdornment: <InputAdornment position="start">₹</InputAdornment>
                              }}
                              size="small"
                            />
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <TextField
                              fullWidth
                              label="Reference Number"
                              value={pm.reference_number || ''}
                              onChange={(e) => handlePaymentReferenceChange(pm.payment_mode_id, e.target.value)}
                              placeholder="Trans ID / Cheque No"
                              size="small"
                            />
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <TextField
                              fullWidth
                              label="Bank Details"
                              value={pm.bank_details || ''}
                              onChange={(e) => handlePaymentBankDetailsChange(pm.payment_mode_id, e.target.value)}
                              placeholder="Bank / Branch"
                              size="small"
                            />
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  ))}
                  
                  {/* Total Settlement */}
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                    <Typography variant="h6" color="primary">
                      Total Settlement: ₹{getTotalSettlement().toFixed(2)}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setCancelDialogOpen(false)} variant="outlined">
            Keep Invoice
          </Button>
          <Button 
            onClick={handleCancelConfirm} 
            variant="contained"
            color="error"
            disabled={!cancelFormData.remarks.trim()}
          >
            Cancel Invoice
          </Button>
        </DialogActions>
      </Dialog>

      {/* Receive Payment Dialog */}
      <Dialog
        open={receivePaymentDialogOpen}
        onClose={() => setReceivePaymentDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #4caf50 0%, #8bc34a 100%)',
          color: 'white',
          pb: 3
        }}>
          <Box display="flex" alignItems="center" gap={2}>
            <Payment sx={{ fontSize: 32 }} />
            <Box>
              <Typography variant="h5" fontWeight="bold">Receive Payment</Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                Invoice #{selectedInvoice?.invoice_no}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          {/* Invoice Summary */}
          {selectedInvoice && (
            <Card sx={{ mb: 3, background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)' }}>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Typography variant="caption" color="text.secondary">Grand Total</Typography>
                    <Typography variant="h6" fontWeight="bold">
                      ₹{Number(selectedInvoice.grand_total || 0).toFixed(2)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="caption" color="text.secondary">Already Paid</Typography>
                    <Typography variant="h6" fontWeight="bold" color="success.main">
                      ₹{(((selectedInvoice.payments?.filter(p => (p.status || 'active') === 'active') || [])
                        .reduce((sum, p) => sum + (parseFloat(p.amount || 0) || 0), 0)) || 0).toFixed(2)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="caption" color="text.secondary">Pending Amount</Typography>
                    <Typography variant="h6" fontWeight="bold" color="error">
                      ₹{(parseFloat(selectedInvoice.grand_total || 0) - (((selectedInvoice.payments?.filter(p => (p.status || 'active') === 'active') || [])
                        .reduce((sum, p) => sum + (parseFloat(p.amount || 0) || 0), 0)) || 0)).toFixed(2)}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          )}

          {/* Payment Mode Selection */}
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Select Payment Modes:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
            {activePaymentModes?.map((mode) => (
              <Chip
                key={mode.id}
                label={mode.name}
                onClick={() => handlePaymentModeToggleForReceive(mode)}
                color={paymentFormData.paymentModes.find(pm => pm.payment_mode_id === mode.id) ? 'primary' : 'default'}
                variant={paymentFormData.paymentModes.find(pm => pm.payment_mode_id === mode.id) ? 'filled' : 'outlined'}
                sx={{ cursor: 'pointer' }}
              />
            ))}
          </Box>

          {/* Payment Details */}
          {paymentFormData.paymentModes.length > 0 && (
            <Box>
              {paymentFormData.paymentModes.map((pm) => (
                <Card key={pm.payment_mode_id} sx={{ mb: 2, border: '1px solid', borderColor: 'primary.light' }}>
                  <CardContent>
                    <Typography variant="subtitle2" gutterBottom color="primary">
                      {pm.payment_mode_name}
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          label="Amount *"
                          type="number"
                          value={pm.amount || ''}
                          onChange={(e) => handlePaymentAmountChangeForReceive(pm.payment_mode_id, e.target.value)}
                          InputProps={{
                            startAdornment: <InputAdornment position="start">₹</InputAdornment>
                          }}
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          label="Reference Number"
                          value={pm.reference_number || ''}
                          onChange={(e) => handlePaymentReferenceChangeForReceive(pm.payment_mode_id, e.target.value)}
                          placeholder="Trans ID / Cheque No"
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          label="Bank Details"
                          value={pm.bank_details || ''}
                          onChange={(e) => handlePaymentBankDetailsChangeForReceive(pm.payment_mode_id, e.target.value)}
                          placeholder="Bank / Branch"
                          size="small"
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              ))}
              
              {/* Total Payment Display */}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                p: 2, 
                bgcolor: 'primary.light', 
                borderRadius: 1,
                color: 'white'
              }}>
                <Typography variant="h6">Total Payment Receiving:</Typography>
                <Typography variant="h6" fontWeight="bold">
                  ₹{getTotalPaymentForReceive().toFixed(2)}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setReceivePaymentDialogOpen(false)} variant="outlined">
            Cancel
          </Button>
          <Button 
            onClick={handleReceivePaymentConfirm} 
            variant="contained"
            color="success"
            disabled={paymentFormData.paymentModes.length === 0 || getTotalPaymentForReceive() === 0}
            startIcon={<Payment />}
          >
            Receive Payment
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default InvoiceList;